/**
 * TenantTableHeader Component
 *
 * Renders the table header row for the tenants list table.
 * Sortable columns are clickable with ascending/descending indicators.
 *
 * @module features/tenants/components/TenantTable/TenantTableHeader
 */

import React from 'react';
import { ArrowUpIcon, ArrowDownIcon } from '../../../../components/common/Icons';

export interface TenantTableHeaderProps {
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (column: string) => void;
}

interface ColumnDef {
  key: string;
  label: string;
  sortable: boolean;
  style: React.CSSProperties;
  title?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Tenant', sortable: true, style: { width: '180px', minWidth: '160px' } },
  { key: 'platform_version', label: 'Version', sortable: true, style: { width: '70px', minWidth: '60px', padding: '8px 4px', textAlign: 'center' }, title: 'Version status' },
  { key: 'status', label: 'Status', sortable: true, style: { width: '100px', minWidth: '90px' } },
  { key: 'health', label: 'Health', sortable: false, style: { width: '100px', minWidth: '90px' } },
  { key: 'isolation_level', label: 'Isolation', sortable: true, style: { width: '90px', minWidth: '80px' }, title: 'Row: Shared tables with row-level filtering (lowest cost). Schema: Dedicated database schema per tenant. Container: Fully isolated instance (highest security).' },
  { key: 'url', label: 'URL', sortable: false, style: { width: '180px', minWidth: '160px' } },
  { key: 'created_at', label: 'Created', sortable: true, style: { width: '90px', minWidth: '80px' } },
  { key: 'actions', label: 'Actions', sortable: false, style: { width: '140px', minWidth: '120px' } },
];

export const TenantTableHeader: React.FC<TenantTableHeaderProps> = ({ sortBy, sortDir, onSort }) => (
  <thead>
    <tr>
      {COLUMNS.map((col) => {
        const isActive = sortBy === col.key;
        const SortIcon = isActive && sortDir === 'asc' ? ArrowUpIcon : ArrowDownIcon;

        return (
          <th
            key={col.key}
            style={{
              ...col.style,
              ...(col.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
            }}
            title={col.title}
            onClick={col.sortable ? () => onSort(col.key) : undefined}
            className={col.sortable ? 'sortable-th' : undefined}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              {col.label}
              {col.sortable && (
                <span
                  className="sort-icon"
                  style={{ opacity: isActive ? 1 : 0, transition: 'opacity 0.15s ease' }}
                >
                  <SortIcon size={12} />
                </span>
              )}
            </span>
          </th>
        );
      })}
    </tr>
  </thead>
);
